import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { AsyncBoundary, Icon } from '@kollez/design-system';
import { apiGet } from '@/lib/api';
import { formatIndianDate } from '@kollez/shared';
import { initialTableParams, toQueryString, type Paginated, type TableQueryParams } from '@/lib/tableQuery';
import { Pagination, SortHeader, TableSearch, ResetButton } from '@/components/DataTable';

/**
 * /platform/users — cross-tenant user directory.
 *   · server-side search across name + email
 *   · role filter, tenant filter (id), status filter
 *   · sortable columns (name, email, role, createdAt, lastLoginAt)
 *   · pagination + reset
 *   · row-click drawer with "View as" (ticket-number gated), Unlock, Resend invite, Sign-out
 *
 * Every action here writes to the audit log server-side. Student PII (phone, resume) is masked
 * in the cross-tenant view — reveal requires a 15-min impersonation with a stated reason.
 */

interface UserRow {
  _id: string;
  fullName: string;
  email: string;
  role: string;
  tenant: string;
  tenantName: string | null;
  status?: string;
  lastLoginAt?: string;
  createdAt?: string;
}

const ROLE_OPTIONS = [
  ['', 'Any role'],
  ['student', 'Student'],
  ['tpo', 'Placement officer'],
  ['principal', 'Principal'],
  ['hod', 'HoD'],
  ['faculty', 'Faculty'],
  ['group_admin', 'Group admin'],
  ['partner_admin', 'Partner admin'],
  ['recruiter', 'Recruiter'],
  ['platform_admin', 'Platform admin'],
  ['platform_operator', 'Platform operator'],
] as const;

const STATUS_OPTIONS = [
  ['', 'Any status'],
  ['active', 'Active'],
  ['locked', 'Locked'],
  ['invited', 'Invited'],
  ['deleting', 'Deleting'],
] as const;

const initial: TableQueryParams = initialTableParams({ sort: 'createdAt', order: 'desc' });

export function PlatformUsersPage() {
  const nav = useNavigate();
  const [params, setParams] = useState<TableQueryParams>(initial);

  // Debounce q so we don't refetch on every keystroke.
  const [qDraft, setQDraft] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setParams((p) => ({ ...p, q: qDraft || undefined, page: 1 })), 300);
    return () => clearTimeout(t);
  }, [qDraft]);

  const query = useQuery({
    queryKey: ['platform', 'users', params],
    queryFn: () => apiGet<Paginated<UserRow>>(`/platform/users?${toQueryString(params)}`),
    placeholderData: (prev) => prev,
  });

  const onSort = (field: string) => {
    setParams((p) => ({
      ...p,
      sort: field,
      order: p.sort === field && p.order === 'asc' ? 'desc' : 'asc',
      page: 1,
    }));
  };

  const reset = () => {
    setQDraft('');
    setParams(initial);
  };

  const filtersDirty = useMemo(
    () => !!(params.q || params.role || params.status || (params.sort !== initial.sort) || (params.order !== initial.order)),
    [params],
  );

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <h1 className="h-page">All users</h1>
          <p className="subhead">Search across every tenant for support. Opening a profile is logged; viewing as a user needs a reason.</p>
        </div>
      </div>

      <div className="notice notice-info">
        <Icon name="shield" size={14} />
        <div>Student phone numbers and resumes are hidden here. Use "View as" with a ticket number when you need them.</div>
      </div>

      <div className="card">
        <div className="filterbar">
          <TableSearch value={qDraft} onChange={setQDraft} placeholder="Name or email" />
          <select
            className="select"
            value={String(params.role ?? '')}
            onChange={(e) => setParams((p) => ({ ...p, role: e.target.value || undefined, page: 1 }))}
          >
            {ROLE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select
            className="select"
            value={String(params.status ?? '')}
            onChange={(e) => setParams((p) => ({ ...p, status: e.target.value || undefined, page: 1 }))}
          >
            {STATUS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <ResetButton onReset={reset} disabled={!filtersDirty} />
          <span className="fb-count">
            {query.data ? `${query.data.total.toLocaleString('en-IN')} total` : ''}
          </span>
        </div>

        <AsyncBoundary kind="table" inCard loading={query.isLoading} error={query.error} empty={!!(query.data && query.data.total === 0)} emptyTitle="No users match">
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <SortHeader label="User" field="fullName" currentSort={params.sort ?? null} currentOrder={params.order ?? 'desc'} onSort={onSort} />
                  <SortHeader label="Role" field="role" currentSort={params.sort ?? null} currentOrder={params.order ?? 'desc'} onSort={onSort} />
                  <th className="hide-mobile">Tenant</th>
                  <SortHeader label="Last seen" field="lastLoginAt" currentSort={params.sort ?? null} currentOrder={params.order ?? 'desc'} onSort={onSort} className="hide-mobile" />
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {query.data?.rows.map((r) => (
                  <tr key={r._id} onClick={() => nav(`/platform/users/${r._id}`)} style={{ cursor: 'pointer' }}>
                    <td>
                      <div className="cell-title">{r.fullName}</div>
                      <div className="cell-sub">{r.email}</div>
                    </td>
                    <td>{r.role}</td>
                    <td className="hide-mobile">{r.tenantName ?? <span className="cell-sub">—</span>}</td>
                    <td className="hide-mobile cell-sub">
                      {r.lastLoginAt ? formatIndianDate(new Date(r.lastLoginAt)) : 'Never'}
                    </td>
                    <td>
                      <span className={`badge ${r.status === 'locked' ? 'badge-danger' : r.status === 'invited' ? '' : 'badge-success'}`}>
                        {r.status ?? 'Active'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {query.data && (
            <Pagination
              page={query.data.page}
              totalPages={query.data.totalPages}
              total={query.data.total}
              limit={query.data.limit}
              onPage={(page) => setParams((p) => ({ ...p, page }))}
              onLimit={(limit) => setParams((p) => ({ ...p, limit, page: 1 }))}
            />
          )}
        </AsyncBoundary>
      </div>

      {/* The row-drawer moved to its own standalone page at /platform/users/:id.
          Deeplinkable, more room for the full profile (permissions, auth
          methods, active sessions, recent audit), and reads via GET
          /platform/users/:id which logs one cross-tenant.read entry — the same
          reason we didn't want the drawer to secretly fetch full profiles. */}
    </div>
  );
}
